import api from "../api";
import ApiConstant from "../../constants/api.constants";
import {
  UserTrafficSourceAuditModel,
  UserTrafficSourceAuditCreateModel,
  UserTrafficSourceAuditUpdateModel,
  UserTrafficSourceAuditFilterModel,
  UserTrafficSourceAuditResponse,
  ProfessionModel,
  TrafficSourceModel,
  UserContactModel,
  ProfessionCreateModel
} from "../../models/UserTrafficSourceAudit/user-traffic-source-audit-model";

/**
 * Service for UserTrafficSourceAudit API operations
 */
const UserTrafficSourceAuditService = {
  /**
   * Get all user traffic source audit records with filtering
   */
  getFiltered: async (
    filter: UserTrafficSourceAuditFilterModel
  ): Promise<UserTrafficSourceAuditResponse> => {
    try {
      const response = await api.post(
        ApiConstant.UserTrafficSourceAudit.getFiltered,
        filter
      );
      return {
        data: response.data?.data || [],
        totalRecords: response.data?.totalRecords || 0
      };
    } catch (error) {
      console.error("Error fetching filtered audit records:", error);
      throw error;
    }
  },

  /**
   * Get all audit records (no pagination)
   */
  getAll: async (): Promise<UserTrafficSourceAuditModel[]> => {
    try {
      const response = await api.get(ApiConstant.UserTrafficSourceAudit.getAll);
      return response.data?.data || [];
    } catch (error) {
      console.error("Error fetching all audit records:", error);
      throw error;
    }
  },

  /**
   * Get a single record by ID
   */
  getById: async (id: number): Promise<UserTrafficSourceAuditModel> => {
    try {
      const response = await api.get(
        `${ApiConstant.UserTrafficSourceAudit.getById}/${id}`
      );
      return response.data?.data || null;
    } catch (error) {
      console.error("Error fetching audit record:", error);
      throw error;
    }
  },

  /**
   * Create a new record
   */
  create: async (
    data: UserTrafficSourceAuditCreateModel
  ): Promise<{ success: boolean; id: number; message: string }> => {
    try {
      const response = await api.post(
        ApiConstant.UserTrafficSourceAudit.create,
        data
      );
      return {
        success: response.data?.success || true,
        id: response.data?.id || 0,
        message: response.data?.message || "Record created successfully"
      };
    } catch (error) {
      console.error("Error creating audit record:", error);
      throw error;
    }
  },

  /**
   * Update existing record
   */
  update: async (
    data: UserTrafficSourceAuditUpdateModel
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.put(
        ApiConstant.UserTrafficSourceAudit.update,
        data
      );
      return {
        success: response.data?.success || true,
        message: response.data?.message || "Record updated successfully"
      };
    } catch (error) {
      console.error("Error updating audit record:", error);
      throw error;
    }
  },

  /**
   * Delete a record
   */
  delete: async (
    id: number
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete(
        `${ApiConstant.UserTrafficSourceAudit.delete}/${id}`
      );
      return {
        success: response.data?.success || true,
        message: response.data?.message || "Record deleted successfully"
      };
    } catch (error) {
      console.error("Error deleting audit record:", error);
      throw error;
    }
  },

  /**
   * Get all professions for dropdown/autocomplete
   */
  getProfessions: async (): Promise<ProfessionModel[]> => {
    try {
      const response = await api.get(
        ApiConstant.UserTrafficSourceAudit.getProfessions
      );
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    } catch (error) {
      console.error("Error fetching professions:", error);
      throw error;
    }
  },

  /**
   * Create or get profession (upsert by name)
   */
  createOrGetProfession: async (
    data: ProfessionCreateModel
  ): Promise<{ success: boolean; id: number; message: string }> => {
    try {
      const response = await api.post(
        ApiConstant.UserTrafficSourceAudit.createOrGetProfession,
        data
      );
      return {
        success: response.data?.isSuccess || true,
        id: parseInt(response.data?.message || '0'),
        message: response.data?.message || ''
      };
    } catch (error) {
      console.error('Error creating/getting profession:', error);
      throw error;
    }
  },

  /**
   * Create or get traffic source (upsert by name)
   */
  createOrGetTrafficSource: async (
    data: { trafficSourceName: string; createdBy: number }
  ): Promise<{ success: boolean; id: number; message: string }> => {
    try {
      const response = await api.post(
        ApiConstant.UserTrafficSourceAudit.createOrGetTrafficSource,
        data
      );
      return {
        success: response.data?.isSuccess || true,
        id: parseInt(response.data?.message || '0'),
        message: response.data?.message || ''
      };
    } catch (error) {
      console.error('Error creating/getting traffic source:', error);
      throw error;
    }
  },

  /**
   * Get admin users (for Introduction Done By dropdown)
   */
  getAdminUsers: async (): Promise<UserContactModel[]> => {
    try {
      const response = await api.get(
        ApiConstant.UserTrafficSourceAudit.getAdminUsers
      );
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  },

  /**
   * Get customers (RoleID == 4) for customer dropdown
   */
  getCustomers: async (keyword?: string): Promise<UserContactModel[]> => {
    try {
      const url = keyword
        ? `${ApiConstant.UserTrafficSourceAudit.getCustomers}?keyword=${encodeURIComponent(keyword)}`
        : ApiConstant.UserTrafficSourceAudit.getCustomers;
      const response = await api.get(url);
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  },

  // Aliases for backward compatibility with Search component
  getTrafficSources: async (): Promise<TrafficSourceModel[]> => {
    try {
      const response = await api.get(ApiConstant.UserTrafficSourceAudit.getTrafficSources);
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    } catch (error) {
      console.error('Error fetching traffic sources:', error);
      throw error;
    }
  },

  getUsers: async (): Promise<UserContactModel[]> => {
    try {
      const response = await api.get(ApiConstant.UserTrafficSourceAudit.getAdminUsers);
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  createProfession: async (data: ProfessionCreateModel): Promise<{ success: boolean; id: number; message: string }> => {
    try {
      const response = await api.post(ApiConstant.UserTrafficSourceAudit.createOrGetProfession, data);
      return { success: response.data?.isSuccess || true, id: parseInt(response.data?.message || '0'), message: response.data?.message || '' };
    } catch (error) { console.error('Error:', error); throw error; }
  }
};

export default UserTrafficSourceAuditService;
